/**
 * Phase 57 Plan 57-04 — PORTAL-04
 *
 * Post-submission confirmation screen (no direct analog — composed from
 * existing primitives). Shows a success card with code/status/project/
 * priority/attachments/notified metadata + a 3-step Triage→In progress→
 * Verify & close process explainer + CTA row.
 *
 * Props:
 *   task       — minimal task data from the create response + optional board
 *                cache enrichment (code, priority, attachments, notified)
 *   project    — project name for copy substitution
 *   projectId  — string project id (for navigation)
 *   onOpenRequest    — navigate to RequestDetail for this task
 *   onSubmitAnother  — reset form (navigate to /requests/new)
 *   onGoToDashboard  — navigate back to the requests list
 */
import { CheckIcon } from '@heroicons/react/24/solid';
import { getTierColor, chipTextColor } from '../../utils/format.js';
import { STATUS_PILL } from './RequestsRail.js';
import { formatDate } from '../../utils/dateFormat';

// TIER label map: order_index 1→URGENT, 2→HIGH, 3→NORMAL, 4→LOW
const TIER_LABEL: Record<number, string> = {
  1: 'URGENT',
  2: 'HIGH',
  3: 'NORMAL',
  4: 'LOW',
};

export interface ConfirmationTask {
  id: number;
  name: string;
  /** Code from the board cache — may be null if not yet reconciled */
  code?: string | null;
  /** orderIndex from the assigned SLA tier — null if unassigned */
  tierOrderIndex?: number | null;
  /** Attachment file count from staged files (client-only) */
  attachmentCount?: number;
  /** PM first name for step 1 body — falls back to "The team" */
  pmFirstName?: string | null;
  /** PM display name (e.g. "Lucie B.") for NOTIFIED section */
  pmShortName?: string | null;
  /** Assignee display name for NOTIFIED section */
  assigneeShortName?: string | null;
}

export interface ConfirmationScreenProps {
  task: ConfirmationTask;
  /** Project display name */
  project: string;
  projectId: string;
  onOpenRequest: () => void;
  onSubmitAnother: () => void;
  onGoToDashboard: () => void;
}

export function ConfirmationScreen({
  task,
  project,
  onOpenRequest,
  onSubmitAnother,
  onGoToDashboard,
}: ConfirmationScreenProps) {
  const submittedPill = STATUS_PILL['submitted'];
  // MKS-351 — dd.mm.yyyy from the shared util.
  const dateStr = formatDate(new Date());

  const pmFirstName = task.pmFirstName ?? 'The team';
  const pmShortName = task.pmShortName ?? 'The team';

  // Priority badge
  const hasTier =
    task.tierOrderIndex !== null && task.tierOrderIndex !== undefined && task.tierOrderIndex >= 1;
  const tierBg = hasTier ? getTierColor(task.tierOrderIndex!) : null;
  const tierText = hasTier ? chipTextColor(tierBg!) : null;
  const tierLabel = hasTier ? (TIER_LABEL[task.tierOrderIndex!] ?? null) : null;

  // Notified list
  const notified: string[] = [];
  if (pmShortName !== 'The team') notified.push(pmShortName + ' (PM)');
  if (task.assigneeShortName) notified.push(task.assigneeShortName + ' (Assignee)');
  const notifiedText =
    notified.length > 0 ? notified.join(' · ') : pmFirstName + ' and the team';

  return (
    <div className="flex-1 overflow-y-auto">
      <div className="flex flex-col items-center py-12 px-8 max-w-2xl mx-auto">
        {/* Success icon */}
        <div className="relative w-16 h-16 bg-green-500 rounded-full flex items-center justify-center mb-6">
          <CheckIcon className="w-8 h-8 text-white" />
          {/* Lightweight sparkle decorations */}
          <span className="absolute -top-3 left-1/2 -translate-x-1/2 text-ms-orange text-xs select-none">
            +
          </span>
          <span className="absolute -bottom-3 left-1/2 -translate-x-1/2 text-ms-orange text-xs select-none">
            +
          </span>
        </div>

        {/* Overline */}
        <p className="text-xs font-semibold uppercase tracking-widest text-ms-orange mb-2">
          REQUEST SUBMITTED
        </p>

        {/* Main heading */}
        <h1 className="text-3xl font-bold text-ms-text-strong text-center mb-3">
          Thanks, your request is in.
        </h1>

        {/* Body copy */}
        <p className="text-sm text-ms-text-secondary text-center max-w-lg">
          {pmFirstName} and the {project} team have been notified. You&#39;ll get an email when
          the status changes —{' '}
          <span className="font-semibold text-ms-text-strong">most requests get a first response within ~4 hours</span>{' '}
          on business days.
        </p>

        {/* Request summary card */}
        <div className="bg-white border border-ms-border rounded-xl p-6 max-w-lg w-full mx-auto mt-6">
          {/* Top row: code + status + timestamp */}
          <div className="flex items-center gap-2 mb-3 flex-wrap">
            {task.code ? (
              <span className="text-ms-orange font-mono text-sm font-semibold">
                {task.code}
              </span>
            ) : (
              <span className="text-ms-text-muted font-mono text-sm">—</span>
            )}
            {submittedPill && (
              <span
                className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${submittedPill.bg} ${submittedPill.text}`}
              >
                <span
                  className="w-1.5 h-1.5 rounded-full flex-shrink-0"
                  style={{ backgroundColor: submittedPill.dot }}
                />
                {submittedPill.label}
              </span>
            )}
            <span className="text-xs text-ms-text-muted ml-auto">
              Just now · {dateStr}
            </span>
          </div>

          {/* Task title */}
          <p className="text-lg font-semibold text-ms-text-strong mb-4">{task.name}</p>

          {/* 2×2 metadata grid */}
          <div className="grid grid-cols-2 gap-3 mb-4">
            {/* PROJECT */}
            <div>
              <p className="text-[10px] font-semibold uppercase tracking-wider text-ms-text-muted mb-1">
                PROJECT
              </p>
              <div className="flex items-center gap-1.5">
                <span className="w-2 h-2 rounded-full bg-ms-orange flex-shrink-0" />
                <span className="text-sm text-ms-text truncate">{project}</span>
              </div>
            </div>

            {/* PRIORITY */}
            <div>
              <p className="text-[10px] font-semibold uppercase tracking-wider text-ms-text-muted mb-1">
                PRIORITY
              </p>
              {tierBg && tierText && tierLabel ? (
                <span
                  className="inline-flex px-2 py-0.5 rounded text-xs font-semibold"
                  style={{ backgroundColor: tierBg, color: tierText }}
                >
                  {tierLabel}
                </span>
              ) : (
                <span className="text-sm text-ms-text-muted">—</span>
              )}
            </div>

            {/* YOUR REFERENCE — deferred field; renders "—" to reserve layout slot */}
            <div>
              <p className="text-[10px] font-semibold uppercase tracking-wider text-ms-text-muted mb-1">
                YOUR REFERENCE
              </p>
              <span className="text-sm text-ms-text-muted">—</span>
            </div>

            {/* ATTACHMENTS */}
            <div>
              <p className="text-[10px] font-semibold uppercase tracking-wider text-ms-text-muted mb-1">
                ATTACHMENTS
              </p>
              <span className="text-sm text-ms-text-muted">
                {task.attachmentCount != null && task.attachmentCount > 0
                  ? `${task.attachmentCount} file${task.attachmentCount === 1 ? '' : 's'}`
                  : 'None'}
              </span>
            </div>
          </div>

          {/* NOTIFIED section */}
          <div>
            <p className="text-[10px] font-semibold uppercase tracking-wider text-ms-text-muted mb-1">
              NOTIFIED
            </p>
            <div className="flex items-center gap-2">
              {/* Avatar stack placeholder */}
              <div className="flex -space-x-1">
                <span className="w-6 h-6 rounded-full bg-ms-surface-2 border border-ms-border flex items-center justify-center text-[10px] font-semibold text-ms-text-secondary">
                  {pmFirstName.charAt(0).toUpperCase()}
                </span>
              </div>
              <span className="text-xs text-ms-text-secondary">{notifiedText}</span>
            </div>
          </div>
        </div>

        {/* 3-step explainer */}
        <div className="grid grid-cols-3 gap-4 max-w-2xl w-full mx-auto mt-6">
          {/* Step 1: Triage */}
          <div className="border border-ms-border rounded-xl p-4">
            <p className="text-[11px] font-semibold uppercase tracking-wider text-ms-text-muted mb-1">
              STEP 1
            </p>
            <p className="text-sm font-semibold text-ms-text-strong mb-1">Triage</p>
            <p className="text-xs text-ms-text-secondary leading-relaxed">
              {pmFirstName} reviews + assigns within a few hours
            </p>
          </div>

          {/* Step 2: In progress */}
          <div className="border border-ms-border rounded-xl p-4">
            <p className="text-[11px] font-semibold uppercase tracking-wider text-ms-text-muted mb-1">
              STEP 2
            </p>
            <p className="text-sm font-semibold text-ms-text-strong mb-1">In progress</p>
            <p className="text-xs text-ms-text-secondary leading-relaxed">
              Owner picks it up, you&#39;ll be looped in on the thread
            </p>
          </div>

          {/* Step 3: Verify & close */}
          <div className="border border-ms-border rounded-xl p-4">
            <p className="text-[11px] font-semibold uppercase tracking-wider text-ms-text-muted mb-1">
              STEP 3
            </p>
            <p className="text-sm font-semibold text-ms-text-strong mb-1">Verify &amp; close</p>
            <p className="text-xs text-ms-text-secondary leading-relaxed">
              We ping you to confirm before marking done
            </p>
          </div>
        </div>

        {/* CTA row */}
        <div className="flex items-center gap-4 mt-8 flex-wrap justify-center">
          <button
            type="button"
            onClick={onOpenRequest}
            className="bg-ms-orange text-white font-medium px-6 py-2.5 rounded-lg hover:opacity-90 transition-opacity"
          >
            Open request →
          </button>
          <button
            type="button"
            onClick={onSubmitAnother}
            className="border border-ms-border text-ms-text-secondary px-4 py-2.5 rounded-lg hover:bg-ms-surface-2 transition-colors"
          >
            Submit another
          </button>
          <button
            type="button"
            onClick={onGoToDashboard}
            className="text-ms-text-secondary text-sm hover:text-ms-text transition-colors"
          >
            Go to dashboard
          </button>
        </div>
      </div>
    </div>
  );
}
